import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { CoachingBenefitsDisclaimer } from './components/CoachingBenefitsDisclaimer';
import { CreateAppointmentForm } from './components/CreateAppointmentForm';
import { LicensedProviderDisclaimer } from './components/LicensedProviderDisclaimer';
import { PaymentDetails } from './components/PaymentDetails';
import { PaymentInfo } from './components/PaymentInfo';
import { Container, PaymentFormWrapper, SupportText } from './Confirm.styled';
import { usePatient } from 'hooks/usePatient/usePatient';
import { isAppointmentType, ValidPromo } from 'kb-shared';
import { LoadedEntities } from 'kb-shared/booking/types/types';
import { Appointment } from 'kb-shared/types';
import { Clinic, TimeSlot } from 'kb-shared/types';
import { AppointmentDetailBlock } from 'screens/Book/components/AppointmentDetailBlock';
import { ConfirmDetails } from 'screens/Book/components/ConfirmDetails';
import { Navigation } from 'screens/Book/components/Navigation/Navigation';
import { StepHeading, StepHeadingContainer } from 'screens/Book/styles';
import { analytics } from 'utilities/analytics';
import { pageUrl, RoutesPath } from 'utilities/pageUrl';
import { hasEmployer } from 'utilities/patient';
import { useBreakpoints } from 'utilities/useBreakpoints';

export const ConfirmStep = ({
  selectedClinic,
  selectedProduct,
  selectedTimeSlot,
  onBookAppointment,
  providerId,
  onBack
}: {
  selectedClinic?: Clinic;
  selectedProduct?: LoadedEntities['selectedProduct'];
  selectedTimeSlot?: TimeSlot;
  onBookAppointment: (appointment: Appointment) => void;
  providerId?: number;
  onBack: () => void;
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { patient } = usePatient();
  const { isDesktop } = useBreakpoints();

  const [paymentTermsChecked, setPaymentTermsChecked] = useState(false);
  const [isForwardClicked, setIsForwardClicked] = useState(false);
  const [isConfirmationDisabled, setIsConfirmationDisabled] = useState<boolean>(true);
  useEffect(() => {
    if (!localStorage.getItem('BOOKING_SUCCEEDED')) return;

    localStorage.removeItem('BOOKING_SUCCEEDED');
    navigate(pageUrl.dashboard());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [validPromo, setValidPromo] = useState<ValidPromo | null>(null);

  useEffect(() => {
    analytics.track(analytics.EVENTS.APPOINTMENT_CONFIRM_STEP);
  }, []);

  // Redirect to the membership activation screen if membership parameters are present in the URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('membership_id') || params.get('enterprise_membership')) {
      navigate(RoutesPath.ENTER_MEMBERSHIP_ACCESS_CODE, {
        replace: true
      });
    }
  }, [navigate, location]);

  // wait until the product is loaded
  if (!selectedProduct) return null;

  const hasMembershipEmployer = hasEmployer(patient);
  const timeSlot = selectedTimeSlot ?? null;
  const clinic = selectedClinic ?? null;

  const onValidPromo = (promo: ValidPromo | null) => {
    setValidPromo(promo);
  };

  return (
    <Container>
      <StepHeadingContainer>
        <StepHeading>
          Pause, Breathe -<br /> Your Journey Begins Now
        </StepHeading>
      </StepHeadingContainer>
      <div>
        {isAppointmentType(selectedProduct.data) && (
          <>
            <AppointmentDetailBlock
              appointmentType={selectedProduct.data}
              timeSlot={timeSlot}
              clinic={clinic}
            />

            <SupportText>
              We are here to support you at every step. Your upcoming session is a key step toward
              understanding your options and determining the best path forward.
            </SupportText>
          </>
        )}
      </div>
      <LicensedProviderDisclaimer product={selectedProduct} />
      <CoachingBenefitsDisclaimer product={selectedProduct} hasEmployer={hasMembershipEmployer} />
      <div>
        <PaymentInfo
          hasEmployerMembership
          appointment={selectedProduct.data}
          discountedPrice={validPromo?.totalCents}
        />
        <PaymentDetails product={selectedProduct} />
        <PaymentFormWrapper>
          <CreateAppointmentForm
            providerId={providerId}
            product={selectedProduct}
            timeSlot={timeSlot}
            onBookAppointment={onBookAppointment}
            handleValidPromo={onValidPromo}
            triggerSubmitAction={isForwardClicked}
            setIsConfirmationDisabled={(isDisabled: boolean) =>
              setIsConfirmationDisabled(isDisabled)
            }
          />
        </PaymentFormWrapper>
      </div>
      <ConfirmDetails
        appointment={selectedProduct.data}
        product={selectedProduct}
        paymentTermsChecked={paymentTermsChecked}
        onPaymentTermsChecked={checked => {
          setPaymentTermsChecked(checked);
          if (isDesktop && checked) window.scrollTo(0, 0);
        }}
      />
      <Navigation
        onBack={onBack}
        step="confirm"
        forwardText="Book & Confirm"
        forwardDisabled={isConfirmationDisabled || !paymentTermsChecked}
        forwardFunction={() => {
          setIsForwardClicked(true);
        }}
      />
    </Container>
  );
};
